/**
 * DEPLETIONS-INVENTORY MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// InventoryPage.jsx — ported from the source app. Adapted for Brand Command:
// Supabase-Auth + RLS service (no MSAL), shared referenceDataService for master
// data (no admin-module import), uuid ids, OneDrive "Refresh Data" replaced by a
// plain "Import" button, and SheetJS loaded on demand. Inventory is a
// clear-and-replace import; Months/Days-on-Hand are computed against the
// depletions-derived average monthly sales baseline.
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  loadInventory,
  loadAvgMonthlySales,
  loadDistributorLastUpdatedInventory,
} from "../utils/depletionService.js";
import {
  listSalesStates,
  listFlavors,
  listSizes,
  listPackSizes,
  listProducts,
} from "../../../shared/utils/referenceDataService.js";
import MobileFilterDrawer from "./MobileFilterDrawer.jsx";
import InventoryChart from "./InventoryChart.jsx";
import ExportOptionsModal from "./ExportOptionsModal.jsx";
import ImportModal from "./ImportModal.jsx";
import HomeButton from "../../../shared/components/HomeButton.jsx";
import "./di-tokens.css";
import "./di-dashboard.css";
import "./DepletionsPage.css";
import "./InventoryPage.css";
import { exportBranded, LOGO_NOTICE } from "../utils/brandedExport.js";
import { buildCategoryBarSvg, svgToPng } from "../utils/chartImage.js";


const GROUP_BY_OPTIONS = [
  { value: "",             label: "All Dimensions" },
  { value: "flavor_size",  label: "Flavor + Size"  },
  { value: "region",       label: "Region"         },
  { value: "state",        label: "State"          },
  { value: "distributor",  label: "Distributor"    },
  { value: "flavor",       label: "Flavor"         },
  { value: "size",         label: "Bottle Size"    },
  { value: "pkg",          label: "Package Size"   },
];

// Chart group-by options — the snapshot bar chart needs a real dimension, so
// drop "All Dimensions" (per-row → one bar per record, unreadable).
const CHART_GROUP_BY_OPTIONS = GROUP_BY_OPTIONS.filter(o => o.value !== "");

function groupRows(rows, groupBy) {
  if (!groupBy) return rows.map(r => ({
    key: String(r.id),
    state:              r.states?.state_name  ?? "—",
    distributor:        r.states?.distributor ?? "—",
    region:             r.states?.region      ?? "—",
    flavor:             r.flavors?.name       ?? "—",
    size:               r.sizes?.name         ?? "—",
    pkg:                r.pack_sizes?.name    ?? "—",
    current_inventory:  r.current_inventory   ?? 0,
    avg_monthly_sales:  r.avg_monthly_sales   ?? 0,
  }));

  const map = new Map();
  for (const r of rows) {
    let key;
    switch (groupBy) {
      case "region":      key = r.states?.region      ?? "—"; break;
      case "state":       key = r.states?.state_name  ?? "—"; break;
      case "distributor": key = r.states?.distributor ?? "—"; break;
      case "flavor":      key = r.flavors?.name       ?? "—"; break;
      case "size":        key = r.sizes?.name         ?? "—"; break;
      case "pkg":         key = r.pack_sizes?.name    ?? "—"; break;
      case "flavor_size": key = `${r.flavors?.name ?? "—"} / ${r.sizes?.name ?? "—"}`; break;
      default:            key = String(r.id);
    }
    if (!map.has(key)) {
      map.set(key, {
        key,
        state:       groupBy === "state"       ? (r.states?.state_name  ?? "—") : (groupBy === "region" ? (r.states?.region ?? "—") : ""),
        distributor: groupBy === "distributor" ? (r.states?.distributor ?? "—") : "",
        region:      groupBy === "region"      ? (r.states?.region      ?? "—") : "",
        flavor:      (groupBy === "flavor" || groupBy === "flavor_size") ? (r.flavors?.name ?? "—") : "",
        size:        (groupBy === "size"   || groupBy === "flavor_size") ? (r.sizes?.name   ?? "—") : "",
        pkg:         groupBy === "pkg"         ? (r.pack_sizes?.name    ?? "—") : "",
        current_inventory: 0,
        avg_monthly_sales: 0,
      });
    }
    map.get(key).current_inventory  += Number(r.current_inventory)  || 0;
    map.get(key).avg_monthly_sales  += Number(r.avg_monthly_sales)  || 0;
  }
  return Array.from(map.values());
}

function getDimCols(groupBy) {
  switch (groupBy) {
    case "region":      return [{ id: "region",      label: "Region"      }];
    case "state":       return [{ id: "state",        label: "State"       }];
    case "distributor": return [{ id: "distributor",  label: "Distributor" }];
    case "flavor":      return [{ id: "flavor",       label: "Flavor"      }];
    case "size":        return [{ id: "size",         label: "Bottle Size" }];
    case "pkg":         return [{ id: "pkg",          label: "Package"     }];
    case "flavor_size": return [{ id: "flavor", label: "Flavor" }, { id: "size", label: "Bottle Size" }];
    default:            return [
      { id: "state",       label: "State"       },
      { id: "distributor", label: "Distributor" },
      { id: "flavor",      label: "Flavor"      },
      { id: "size",        label: "Bottle Size" },
      { id: "pkg",         label: "Package"     },
    ];
  }
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function InventoryPage({ onHome }) {
  const [rows,          setRows]          = useState([]);
  const [states,        setStates]        = useState([]);
  const [flavors,       setFlavors]       = useState([]);
  const [sizes,         setSizes]         = useState([]);
  const [packSizes,     setPackSizes]     = useState([]);
  const [products,      setProducts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [toast,         setToast]         = useState(null);
  const [luOpen,        setLuOpen]        = useState(false);
  const [luData,        setLuData]        = useState(null);
  const [luLoading,     setLuLoading]     = useState(false);
  const [viewMode,      setViewMode]      = useState("grid");
  const [exportOpen,    setExportOpen]    = useState(false);
  const [importOpen,    setImportOpen]    = useState(false);

  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const [lowInvModalOpen,  setLowInvModalOpen]  = useState(false);
  const [lowInvMaxDays,    setLowInvMaxDays]    = useState(null);
  const [lowInvInputValue, setLowInvInputValue] = useState("30");

  const [mohMonths,       setMohMonths]       = useState(3);
  const [avgSalesMap,     setAvgSalesMap]     = useState({});
  const [avgSalesLoading, setAvgSalesLoading] = useState(true);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    let cancelled = false;
    setAvgSalesLoading(true);

    const MAX_ATTEMPTS = 4;
    const DELAYS = [0, 2000, 4000, 6000];

    const attempt = (n) => {
      setTimeout(() => {
        if (cancelled) return;
        loadAvgMonthlySales(mohMonths)
          .then(map => {
            if (cancelled) return;
            setAvgSalesMap(map);
            setAvgSalesLoading(false);
          })
          .catch(() => {
            if (cancelled) return;
            if (n + 1 < MAX_ATTEMPTS) {
              attempt(n + 1);
            } else {
              setAvgSalesLoading(false);
              showToast("Could not load Avg Monthly Sales — try refreshing.", "error");
            }
          });
      }, DELAYS[n]);
    };

    attempt(0);
    return () => { cancelled = true; };
  }, [mohMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  const [colWidths, setColWidths] = useState({});
  const INV_MIN_W   = 50;
  const INV_DEF_W   = { state: 120, distributor: 160, region: 140, flavor: 130, size: 80, pkg: 100, inventory: 130, moh: 130 };
  const startResize = useCallback((col, e) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[col] ?? INV_DEF_W[col] ?? 100;
    const onMove = mv => setColWidths(prev => ({ ...prev, [col]: Math.max(INV_MIN_W, startW + mv.clientX - startX) }));
    const onUp   = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [colWidths]); // eslint-disable-line react-hooks/exhaustive-deps

  const [groupBy,           setGroupBy]           = useState("");
  const [filterRegion,      setFilterRegion]      = useState("");
  const [filterState,       setFilterState]       = useState("");
  const [filterDistributor, setFilterDistributor] = useState("");
  const [filterTag,         setFilterTag]         = useState("");
  const [filterFlavor,      setFilterFlavor]      = useState("");
  const [filterSize,        setFilterSize]        = useState("");
  const [filterPackSize,    setFilterPackSize]    = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [inv, sts, flvs, szs, pkgs, prods] = await Promise.all([
        loadInventory({
          flavorId:   filterFlavor   || undefined,
          sizeId:     filterSize     || undefined,
          packSizeId: filterPackSize || undefined,
        }),
        listSalesStates(),
        listFlavors(),
        listSizes(),
        listPackSizes(),
        listProducts(),
      ]);
      setRows(inv);
      setStates(sts);
      setFlavors(flvs);
      setSizes(szs);
      setPackSizes(pkgs);
      setProducts(prods);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [filterFlavor, filterSize, filterPackSize]);

  useEffect(() => { load(); }, [load]);

  const regionOptions = useMemo(() =>
    [...new Set(states.map(s => s.region).filter(Boolean))].sort()
  , [states]);

  const filteredStates = useMemo(() => {
    const base = filterRegion ? states.filter(s => s.region === filterRegion) : states;
    const seen = new Set();
    return base.filter(s => {
      if (seen.has(s.state_name)) return false;
      seen.add(s.state_name);
      return true;
    });
  }, [states, filterRegion]);

  const distributorOptions = useMemo(() =>
    [...new Set(rows.map(r => r.states?.distributor).filter(Boolean))].sort()
  , [rows]);

  // Tags come from the account's own SKUs (System Settings → Product SKUs).
  // This list used to be four hardcoded flavor names, which offered every tenant
  // tags they had never used and hid the ones they had.
  const tagOptions = useMemo(
    () => [...new Set(products.flatMap((p) => (Array.isArray(p.tag) ? p.tag : p.tag ? [p.tag] : [])))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b)),
    [products]
  );

  const tagMap = useMemo(() => {
    const map = {};
    products.forEach(p => {
      const key = `${p.flavor_id}|${p.size_id}|${p.pack_size_id}`;
      map[key] = Array.isArray(p.tag) ? p.tag : p.tag ? [p.tag] : [];
    });
    return map;
  }, [products]);

  const filtered = useMemo(() => rows.filter(r => {
    if (filterState       && r.states?.state_name    !== filterState)       return false;
    if (filterRegion      && r.states?.region        !== filterRegion)      return false;
    if (filterDistributor && r.states?.distributor   !== filterDistributor) return false;
    if (filterTag) {
      const tags = tagMap[`${r.flavors?.id}|${r.sizes?.id}|${r.pack_sizes?.id}`] ?? [];
      if (!tags.includes(filterTag)) return false;
    }
    if (filterFlavor      && String(r.flavors?.id)    !== filterFlavor)     return false;
    if (filterSize        && String(r.sizes?.id)      !== filterSize)        return false;
    if (filterPackSize    && String(r.pack_sizes?.id) !== filterPackSize)   return false;
    return true;
  }), [rows, filterState, filterRegion, filterDistributor, filterTag, filterFlavor, filterSize, filterPackSize, tagMap]);

  // Unfiltered-by-days totals. These feed the "N total units" line in the
  // header, which is a portfolio figure and should NOT move when someone
  // narrows to low stock. The grid's and the export's Total row must instead
  // total what is actually listed — see totalsShown below.
  const totalInventory = useMemo(() =>
    filtered.reduce((s, r) => s + (Number(r.current_inventory) || 0), 0)
  , [filtered]);

  const enrichedFiltered = useMemo(() =>
    filtered.map(r => ({
      ...r,
      avg_monthly_sales: avgSalesMap[
        `${r.states?.id}|${r.flavors?.id}|${r.sizes?.id}|${r.pack_sizes?.id}`
      ] ?? 0,
    }))
  , [filtered, avgSalesMap]);

  const grouped = useMemo(() => groupRows(enrichedFiltered, groupBy), [enrichedFiltered, groupBy]);

  const dimCols = useMemo(() => getDimCols(groupBy), [groupBy]);

  const handleSort = useCallback((col) => {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  }, [sortCol]);

  const sortedGrouped = useMemo(() => {
    if (!sortCol) return grouped;
    return [...grouped].sort((a, b) => {
      let av, bv;
      if (sortCol === "moh" || sortCol === "doh") {
        av = a.avg_monthly_sales > 0 ? a.current_inventory / a.avg_monthly_sales : -Infinity;
        bv = b.avg_monthly_sales > 0 ? b.current_inventory / b.avg_monthly_sales : -Infinity;
      } else if (sortCol === "inventory" || sortCol === "avgSales") {
        av = sortCol === "inventory" ? (a.current_inventory || 0) : (a.avg_monthly_sales || 0);
        bv = sortCol === "inventory" ? (b.current_inventory || 0) : (b.avg_monthly_sales || 0);
      } else {
        av = (a[sortCol] || "").toString().toLowerCase();
        bv = (b[sortCol] || "").toString().toLowerCase();
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
  }, [grouped, sortCol, sortDir]);

  const hasFilters = filterRegion || filterState || filterDistributor || filterTag || filterFlavor || filterSize || filterPackSize || lowInvMaxDays !== null;

  const clearFilters = () => {
    setFilterRegion(""); setFilterState(""); setFilterDistributor("");
    setFilterTag(""); setFilterFlavor(""); setFilterSize(""); setFilterPackSize(""); setGroupBy("");
    setLowInvMaxDays(null); setLowInvInputValue("30");
  };

  const displayRows = useMemo(() => {
    if (lowInvMaxDays === null) return sortedGrouped;
    return sortedGrouped.filter(r => {
      if (!r.avg_monthly_sales || r.avg_monthly_sales === 0) return false;
      const doh = Math.round(r.current_inventory / r.avg_monthly_sales * 30);
      return doh <= lowInvMaxDays;
    });
  }, [sortedGrouped, lowInvMaxDays]);

  // The Total row totals THE ROWS ON SCREEN. Previously it reused
  // totalInventory / totalAvgMonthlySales, which are computed from `filtered` /
  // `grouped` — i.e. before the Low Inventory day threshold is applied in
  // displayRows above. Turning on "Low Inventory ≤45d" therefore listed 8 rows
  // worth 689 units under a Total reading 11,925, in the grid and in the
  // exported .xlsx alike. A total that doesn't add up its own column is worse
  // than no total. Found capturing the low-stock report for the website.
  const totalsShown = useMemo(() => {
    const inv = displayRows.reduce((s, r) => s + (Number(r.current_inventory) || 0), 0);
    const avg = displayRows.reduce((s, r) => s + (Number(r.avg_monthly_sales) || 0), 0);
    return { inv, avg, moh: avg > 0 ? inv / avg : null, doh: avg > 0 ? Math.round((inv / avg) * 30) : null };
  }, [displayRows]);

  const fmtLU = d => {
    if (!d) return "No data";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      + " at "
      + dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  const handleLastUpdated = async () => {
    setLuOpen(true);
    setLuLoading(true);
    setLuData(null);
    try {
      const data = await loadDistributorLastUpdatedInventory(filterDistributor);
      setLuData(data);
    } catch (e) {
      setLuData({ error: e.message });
    } finally {
      setLuLoading(false);
    }
  };

  const handleExport = async ({ includeChart, chartGroupBy } = {}) => {
    const columns = [
      // 22, not brandedExport's default 18: distributor names are the widest
      // thing in this export and 18 clipped "Pacific Crest Beverages" and
      // "Sonoran Wine & Spirits" mid-word.
      ...dimCols.map(c => ({ label: c.label, key: c.id, kind: "dim", width: 22 })),
      // Widths are in Excel character units and must clear the HEADER, not the
      // values — these headers are all longer than the numbers under them, and
      // at 16/16/15/14 Excel clipped them to "urrent Inventory", "vg Monthly
      // Sales" and "onths on Hand" in the exported file.
      { label: "Current Inventory", key: "inv", kind: "num", width: 19 },
      { label: "Avg Monthly Sales", key: "avg", kind: "num", width: 19, numFmt: "#,##0.0" },
      { label: "Months on Hand", key: "moh", kind: "num", width: 17, numFmt: "#,##0.0" },
      { label: "Days on Hand", key: "doh", kind: "num", width: 15 },
    ];
    const rows = displayRows.map(r => ({
      ...Object.fromEntries(dimCols.map(c => [c.id, r[c.id] ?? "—"])),
      inv: r.current_inventory || null,
      avg: r.avg_monthly_sales > 0 ? +r.avg_monthly_sales.toFixed(1) : null,
      moh: r.avg_monthly_sales > 0 ? +(r.current_inventory / r.avg_monthly_sales).toFixed(1) : null,
      doh: r.avg_monthly_sales > 0 ? Math.round(r.current_inventory / r.avg_monthly_sales * 30) : null,
    }));
    const totals = displayRows.length > 1 ? {
      [dimCols[0].id]: "Total",
      inv: totalsShown.inv || null,
      avg: totalsShown.avg > 0 ? +totalsShown.avg.toFixed(1) : null,
      moh: totalsShown.moh !== null ? +totalsShown.moh.toFixed(1) : null,
      doh: totalsShown.doh,
    } : null;

    const nameOf = (list, id) => (id ? (list.find(x => String(x.id) === String(id))?.name || "") : "");
    const filtersArr = [
      ["Region", filterRegion], ["State", filterState], ["Distributor", filterDistributor],
      ["TAG", filterTag], ["Flavor", nameOf(flavors, filterFlavor)],
      ["Bottle Size", nameOf(sizes, filterSize)], ["Package", nameOf(packSizes, filterPackSize)],
      ["Group By", groupBy ? (GROUP_BY_OPTIONS.find(o => o.value === groupBy)?.label || "") : ""],
      ["Low Inventory", lowInvMaxDays !== null ? `≤ ${lowInvMaxDays} days on hand` : ""],
    ].filter(([, v]) => v);

    // Optional chart worksheet (2nd tab) — a snapshot bar per category. Regroup
    // by the chart's chosen dimension (which may differ from the page's group-by).
    let chart = null;
    if (includeChart) {
      const gb = chartGroupBy || "flavor_size";
      const chartCols = getDimCols(gb);
      const chartGroups = groupRows(enrichedFiltered, gb)
        .map(g => ({
          label: chartCols.map(c => g[c.id]).filter(Boolean).join(" ") || "—",
          inventory: Number(g.current_inventory) || 0,
        }))
        .filter(g => g.inventory > 0)
        .sort((a, b) => b.inventory - a.inventory)
        .slice(0, 24); // keep the chart readable
      if (chartGroups.length) {
        const gbLabel = CHART_GROUP_BY_OPTIONS.find(o => o.value === gb)?.label || "";
        const svg = buildCategoryBarSvg({
          categories: chartGroups.map(g => g.label),
          values: chartGroups.map(g => g.inventory),
          axisLabel: "UNITS",
          title: `Current Inventory${gbLabel ? ` by ${gbLabel}` : ""}`,
        });
        const png = await svgToPng(svg, 920, 470);
        if (png) chart = { name: "Inventory Chart", base64: png.base64, width: png.width, height: png.height };
      }
    }

    const res = await exportBranded({
      title: "Inventory",
      subtitle: `On-hand snapshot · MOH window ${mohMonths} mo`,
      filters: filtersArr,
      columns, rows, totals,
      sheetName: "Inventory",
      note: "Current on-hand inventory in physical units. Months/Days on Hand normalized to the avg-monthly-sales baseline. Generated by Brand Command — Depletions & Inventory.",
      chart,
    });
    return LOGO_NOTICE[res?.logo] || null;
  };

  const fmtNum = n => Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <div className="di-module dep-page">

      {/* ── Toolbar ── */}
      <div className="dashboard-toolbar">
        <div className="toolbar-left">
          <HomeButton onClick={onHome} />
          <span className="toolbar-title">Inventory</span>
          {!loading && (
            <span className="toolbar-refresh-time">
              {filtered.length.toLocaleString()} rows
              {totalInventory > 0 && (
                <> &middot; <span style={{ color: "var(--o-text)", fontWeight: 600 }}>{fmtNum(totalInventory)}</span> total units</>
              )}
            </span>
          )}
        </div>
        <div className="toolbar-right">
          <button
            className="toolbar-btn"
            style={lowInvMaxDays !== null ? { background: "var(--o-fill)", color: "var(--o-on-fill)", borderColor: "var(--o-fill-border)", fontWeight: 600 } : {}}
            onClick={() => { setLowInvInputValue(lowInvMaxDays !== null ? String(lowInvMaxDays) : "30"); setLowInvModalOpen(true); }}
            title="Filter to low-stock rows by days on hand"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/>
            </svg>
            {lowInvMaxDays !== null ? `Low Inventory ≤${lowInvMaxDays}d` : "Low Inventory"}
          </button>
          <button className="toolbar-btn" onClick={handleLastUpdated}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            Last Updated
          </button>
          <button className="toolbar-btn" onClick={() => setImportOpen(true)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Import
          </button>
          <button className="toolbar-btn export-btn" onClick={() => setExportOpen(true)} disabled={grouped.length === 0}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* ── Filter bar — wrapped in MobileFilterDrawer for mobile collapse ── */}
      <MobileFilterDrawer
        summary={[
          filterRegion, filterState, filterDistributor, filterTag,
          filterFlavor, filterSize, filterPackSize,
        ].filter(Boolean).join(" · ")}
        activeCount={
          [filterRegion, filterState, filterDistributor, filterTag, filterFlavor, filterSize, filterPackSize].filter(Boolean).length
        }
        onReset={clearFilters}
      >
      <div className="selector-bar">
        <div className="selector-group">
          <label className="selector-label">Region</label>
          <select className="selector-select" value={filterRegion} onChange={e => { setFilterRegion(e.target.value); setFilterState(""); }}>
            <option value="">— All Regions —</option>
            {regionOptions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">State</label>
          <select className="selector-select" value={filterState} onChange={e => setFilterState(e.target.value)}>
            <option value="">— All States —</option>
            {filteredStates.map(s => <option key={s.state_name} value={s.state_name}>{s.state_name}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">Distributor</label>
          <select className="selector-select" value={filterDistributor} onChange={e => setFilterDistributor(e.target.value)}>
            <option value="">— All Distributors —</option>
            {distributorOptions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">TAG</label>
          <select className="selector-select" value={filterTag} onChange={e => setFilterTag(e.target.value)}>
            <option value="">— All TAGs —</option>
            {tagOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">Flavor</label>
          <select className="selector-select" value={filterFlavor} onChange={e => setFilterFlavor(e.target.value)}>
            <option value="">— All Flavors —</option>
            {flavors.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">Bottle Size</label>
          <select className="selector-select" value={filterSize} onChange={e => setFilterSize(e.target.value)}>
            <option value="">— All Sizes —</option>
            {sizes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">Package</label>
          <select className="selector-select" value={filterPackSize} onChange={e => setFilterPackSize(e.target.value)}>
            <option value="">— All Packages —</option>
            {packSizes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="dep-groupby-wrap">
          <div className="selector-group">
            <label className="selector-label dep-groupby-label">Group By</label>
            <select className="selector-select dep-groupby-select" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              {GROUP_BY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="selector-group" style={{ marginLeft: 6 }}>
            <label className="selector-label dep-groupby-label">MOH — # of Months</label>
            <select className="selector-select dep-groupby-select" value={mohMonths} onChange={e => setMohMonths(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
        {hasFilters && (
          <button className="selector-clear" onClick={clearFilters}>Clear</button>
        )}
        <div className="view-toggle">
          <button
            className={`view-toggle-btn${viewMode === "grid" ? " view-toggle-btn-active" : ""}`}
            onClick={() => setViewMode("grid")} title="Grid view"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
            </svg>
            Grid
          </button>
          <button
            className={`view-toggle-btn${viewMode === "chart" ? " view-toggle-btn-active" : ""}`}
            onClick={() => setViewMode("chart")} title="Chart view"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 20 9 4 6 12 2 12"/>
            </svg>
            Chart
          </button>
        </div>
      </div>
      </MobileFilterDrawer>

      {/* ── Content ── */}
      <div className="dashboard-content">
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Loading inventory data…</div>
            <div className="loading-sub">Connecting to database</div>
          </div>
        )}
        {!loading && error && (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <div className="error-title">Failed to load data</div>
            <div className="error-msg">{error}</div>
            <button className="error-retry" onClick={load}>Retry</button>
          </div>
        )}
        {!loading && !error && viewMode === "chart" && (
          <InventoryChart rows={displayRows} dimCols={dimCols} />
        )}
        {!loading && !error && viewMode === "grid" && (
          rows.length === 0 ? (
            <div className="grid-prompt">
              <div className="grid-prompt-icon">📦</div>
              <div className="grid-prompt-text">
                No inventory data yet. Connect a Google Sheet in <strong>System Settings → Sync Settings</strong> to bring inventory in.
              </div>
            </div>
          ) : (
            <div className="dep-grid-wrap">
              <div className="dep-grid-scroll">
                <table className="forecast-table" style={{ tableLayout: "fixed" }}>
                  <colgroup>
                    {dimCols.map(c => (
                      <col key={c.id} style={{ width: colWidths[c.id] ?? INV_DEF_W[c.id] ?? 120 }} />
                    ))}
                    <col style={{ width: colWidths.inventory ?? INV_DEF_W.inventory }} />
                    <col style={{ width: colWidths.avgSales ?? INV_DEF_W.moh }} />
                    <col style={{ width: colWidths.moh ?? INV_DEF_W.moh }} />
                    <col style={{ width: colWidths.doh ?? INV_DEF_W.moh }} />
                  </colgroup>
                  <thead>
                    <tr>
                      {dimCols.map(c => (
                        <th key={c.id} style={{ position: "relative", cursor: "pointer", userSelect: "none" }} onClick={() => handleSort(c.id)}>
                          {c.label}
                          {sortCol === c.id ? (sortDir === "asc" ? " ↑" : " ↓") : <span style={{ opacity: 0.3 }}> ↕</span>}
                          <span className="col-resize-handle" onMouseDown={e => startResize(c.id, e)} />
                        </th>
                      ))}
                      <th className="th-right" style={{ position: "relative", cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("inventory")}>
                        Current Inventory
                        {sortCol === "inventory" ? (sortDir === "asc" ? " ↑" : " ↓") : <span style={{ opacity: 0.3 }}> ↕</span>}
                        <span className="col-resize-handle" onMouseDown={e => startResize("inventory", e)} />
                      </th>
                      <th className="th-right" style={{ position: "relative", cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("avgSales")}>
                        Avg Monthly Sales
                        {sortCol === "avgSales" ? (sortDir === "asc" ? " ↑" : " ↓") : <span style={{ opacity: 0.3 }}> ↕</span>}
                        <span className="col-resize-handle" onMouseDown={e => startResize("avgSales", e)} />
                      </th>
                      <th className="th-right" style={{ position: "relative", cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("moh")}>
                        Months on Hand (normalized)
                        {sortCol === "moh" ? (sortDir === "asc" ? " ↑" : " ↓") : <span style={{ opacity: 0.3 }}> ↕</span>}
                        <span className="col-resize-handle" onMouseDown={e => startResize("moh", e)} />
                      </th>
                      <th className="th-right" style={{ position: "relative", cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("doh")}>
                        Days on Hand (normalized)
                        {sortCol === "doh" ? (sortDir === "asc" ? " ↑" : " ↓") : <span style={{ opacity: 0.3 }}> ↕</span>}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((r, i) => (
                      <tr key={r.key} className={i % 2 !== 0 ? "stripe" : ""}>
                        {dimCols.map(c => (
                          <td key={c.id}>{r[c.id] || "—"}</td>
                        ))}
                        <td className="dep-td-cases th-right">{fmtNum(r.current_inventory)}</td>
                        <td className="dep-td-cases th-right">{avgSalesLoading ? "…" : fmtNum(r.avg_monthly_sales)}</td>
                        <td className="dep-td-cases th-right">
                          {avgSalesLoading ? "…" : r.avg_monthly_sales > 0 ? (r.current_inventory / r.avg_monthly_sales).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : "—"}
                        </td>
                        <td className="dep-td-cases th-right">
                          {avgSalesLoading ? "…" : r.avg_monthly_sales > 0 ? Math.round(r.current_inventory / r.avg_monthly_sales * 30).toLocaleString("en-US") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {displayRows.length > 1 && (
                    <tfoot>
                      <tr className="inv-total-row">
                        {dimCols.map((c, i) => (
                          <td key={c.id} className="inv-total-label">
                            {i === 0 ? `Total (${displayRows.length.toLocaleString()} rows)` : ""}
                          </td>
                        ))}
                        <td className="dep-td-cases inv-total-label th-right">{fmtNum(totalsShown.inv)}</td>
                        <td className="dep-td-cases inv-total-label th-right">{avgSalesLoading ? "…" : fmtNum(totalsShown.avg)}</td>
                        <td className="dep-td-cases inv-total-label th-right">
                          {avgSalesLoading ? "…" : totalsShown.moh !== null ? totalsShown.moh.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : "—"}
                        </td>
                        <td className="dep-td-cases inv-total-label th-right">
                          {avgSalesLoading ? "…" : totalsShown.doh !== null ? totalsShown.doh.toLocaleString("en-US") : "—"}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )
        )}
      </div>

      <ExportOptionsModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        groupByOptions={CHART_GROUP_BY_OPTIONS}
        defaultGroupBy={groupBy && groupBy !== "" ? groupBy : "flavor_size"}
        chartNote="Adds a second worksheet tab with a current-inventory bar chart."
        onConfirm={handleExport}
      />

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        dataset="inventory"
        states={states}
        flavors={flavors}
        sizes={sizes}
        packSizes={packSizes}
        products={products}
        onImported={async ({ inserted, skipped, noSkuRows = 0, warning }) => {
          await load();
          // backlog APP-334: the import did not complete cleanly. The modal is
          // still open with the full explanation — the toast must not sit
          // behind it claiming a clean import.
          if (warning) { showToast(warning, "error"); return; }
          // noSkuRows (backlog item 104) is not the same failure as `skipped`:
          // those rows WERE written, attached to no product. Both must colour
          // the toast, or the import still reads as clean.
          showToast(
            `Imported ${inserted.toLocaleString()} inventory row${inserted === 1 ? "" : "s"}`
            + (skipped ? ` · ${skipped} row${skipped === 1 ? "" : "s"} skipped` : "")
            + (noSkuRows ? ` · ⚠ ${noSkuRows.toLocaleString()} row${noSkuRows === 1 ? "" : "s"} with no SKU match` : ""),
            (skipped || noSkuRows) ? "error" : "success",
          );
        }}
      />

      {toast && (
        <div className={`dep-toast${toast.type === "error" ? " dep-toast-error" : ""}`}>
          {toast.msg}
        </div>
      )}

      {lowInvModalOpen && (
        <div className="dep-backdrop" onClick={() => setLowInvModalOpen(false)}>
          <div className="dep-modal" style={{ maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div className="dep-modal-header">
              <span className="dep-modal-title">Low Inventory Filter</span>
              <button className="dep-modal-close" onClick={() => setLowInvModalOpen(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="dep-modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: "var(--heading)" }}>Max Days on Hand</label>
                <input
                  type="number"
                  min="1"
                  value={lowInvInputValue}
                  onChange={e => setLowInvInputValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      const val = parseInt(lowInvInputValue, 10);
                      if (!isNaN(val) && val > 0) { setLowInvMaxDays(val); setLowInvModalOpen(false); }
                    }
                  }}
                  style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--field)", color: "var(--heading)", fontSize: 14, width: "100%", boxSizing: "border-box" }}
                  autoFocus
                />
                <p style={{ fontSize: 12, color: "var(--muted)", margin: 0 }}>
                  Shows rows where Days on Hand ≤ this value. Rows with no avg monthly sales are excluded.
                </p>
              </div>
            </div>
            <div className="dep-modal-footer">
              <button className="dep-btn-cancel" onClick={() => setLowInvModalOpen(false)}>Cancel</button>
              <button
                className="dep-btn-primary"
                onClick={() => {
                  const val = parseInt(lowInvInputValue, 10);
                  if (!isNaN(val) && val > 0) { setLowInvMaxDays(val); setLowInvModalOpen(false); }
                }}
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {luOpen && (
        <div className="dep-backdrop" onClick={() => setLuOpen(false)}>
          <div className="dep-modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div className="dep-modal-header">
              <span className="dep-modal-title">Last Updated / Modified</span>
              <button className="dep-modal-close" onClick={() => setLuOpen(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="dep-modal-body">
              {luLoading ? (
                <div style={{ color: "var(--muted)", fontSize: 14 }}>Loading…</div>
              ) : luData?.error ? (
                <div style={{ color: "var(--error)", fontSize: 14 }}>{luData.error}</div>
              ) : !luData?.length ? (
                <div style={{ color: "var(--muted)", fontSize: 14 }}>No data found.</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "6px 10px", color: "var(--o-text)", fontWeight: 600, borderBottom: "1px solid var(--border)" }}>Distributor</th>
                      <th style={{ textAlign: "left", padding: "6px 10px", color: "var(--o-text)", fontWeight: 600, borderBottom: "1px solid var(--border)" }}>Last Updated</th>
                      <th style={{ textAlign: "left", padding: "6px 10px", color: "var(--o-text)", fontWeight: 600, borderBottom: "1px solid var(--border)" }}>Last Modified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {luData.map(row => (
                      <tr key={row.distributor}>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--heading)", fontWeight: 500 }}>{row.distributor}</td>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--muted)" }}>{fmtLU(row.last_inventory_update)}</td>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--muted)" }}>{fmtLU(row.last_inventory_modified)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
